import { EntityNotFound } from '@common/EntityNotFound';
import { CodeGenerationContextProvider } from '@common/git/CodeGeneration/context';
import { GitInfo } from '@common/git/GitInfo';
import { ProjectContextProvider } from '@common/git/Project/context';
import { useProjectState } from '@common/git/Project/hooks';
import { PageHeader } from '@common/Header/PageHeader';
import { SubgraphDetailType } from '@common/queries/subgraph';
import { Entity } from '@common/types/Entity';
import { Private_Routes } from '@common/url';
import { EntityEvents } from '@common/user-analytics';
import { titleCase } from '@common/utils/metadataUtils';
import { ExtractUrlPathVariables } from '@prophecy/interfaces/generic';
import { EntityDetailQuery } from '@prophecy/queries/common/queries/common';
import { theme } from '@prophecy/theme';
import { TabPane, Stack, Tabs, Container, Centered, Spinner } from '@prophecy/ui-v3';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMixPanel } from '../../../context/mixpanel/context';
import { useGraphQlQuery } from '../../../data/util';
import { InfoEntity } from '../components/EntityInfo';
import { EntityTableWithCreateButton } from '../components/EntityTableWithCreateButton';
import { Footer } from '../config/styled';
import { SubgraphFooterSchema, SubgraphInfoSchema, SubgraphPageLayout } from '../config/subgraph';
import { getMetadataDefaultCrumbs } from './MetadataDetailHeader';
import { CodeTab } from './ProjectDetail/CodeTab';

const StyledInfoEntity = styled(InfoEntity)`
  height: auto;
` as typeof InfoEntity;
export default function Page() {
  const params = useParams<ExtractUrlPathVariables<typeof Private_Routes.Subgraph.tab.url>>();
  const tab = params.tab as string;
  const uid = decodeURIComponent(params.uid as string);

  const variables = { uid };
  const { data, isLoading } = useGraphQlQuery<SubgraphDetailType>(
    [Entity.Subgraph, Entity.Subgraph + 'Detail', variables],
    EntityDetailQuery(Entity.Subgraph),
    undefined,
    variables
  );
  const subgraph = data?.Subgraph;
  if (isLoading) {
    return (
      <Centered>
        <Spinner />
      </Centered>
    );
  }
  if (!subgraph) return <EntityNotFound uid={uid} entity={Entity.Subgraph} projectContext={true} />;
  return (
    <ProjectContextProvider projectId={subgraph?.project?._id}>
      <CodeGenerationContextProvider>
        <SubgraphDetail uid={uid} subgraph={subgraph} tab={tab} />
      </CodeGenerationContextProvider>
    </ProjectContextProvider>
  );
}
function SubgraphDetail({
  uid,
  subgraph,
  tab
}: {
  uid: string;
  tab: string;
  subgraph: SubgraphDetailType['Subgraph'];
}) {
  const [activeTab, setActiveTab] = useState(titleCase(tab) || SubgraphPageLayout.firstTab);

  const { projectId, projectName, currentBranch: branch, isMainBranch: readOnly } = useProjectState();
  const { track } = useMixPanel();

  useEffect(() => {
    track(EntityEvents.common.metadata.entityOpen, {
      uid,
      name: subgraph.name,
      entity: Entity.Subgraph,
      url: window.location.href
    });
  }, [track, subgraph.name, uid]);

  const changeTab = (tab: string) => {
    setActiveTab(tab);
    track(EntityEvents.common.metadata.tabChange, {
      uid,
      name: subgraph.name,
      tab,
      entity: Entity.Subgraph,
      url: window.location.href
    });
  };

  const getTabDetail = (title: string) => {
    if (title === 'Info') {
      return (
        <Stack gap={theme.spaces.x16} height='100%' align='space-between'>
          <Stack gap={theme.spaces.x16} grow='1'>
            <StyledInfoEntity
              branch={branch}
              hasCodegenContext={false}
              projectId={projectId}
              readOnly={readOnly}
              schema={SubgraphInfoSchema()}
              hideFooter={true}
              variables={{ uid }}
            />
            {projectId && (
              <EntityTableWithCreateButton
                uid={uid}
                entity={Entity.Subgraph}
                childEntity={Entity.Pipeline}
                projectId={projectId}
              />
            )}
          </Stack>
          <StyledInfoEntity
            branch={branch}
            projectId={projectId}
            hasCodegenContext={false}
            readOnly={readOnly}
            schema={SubgraphFooterSchema()}
            variables={{ uid }}
          />
        </Stack>
      );
    } else if (title === 'Code') {
      return projectId && <CodeTab projectId={projectId} uri={`/${subgraph.path}`}></CodeTab>;
    }
  };

  return (
    <ProjectContextProvider projectId={projectId}>
      <CodeGenerationContextProvider>
        <Stack width='100%' height='100%' align='space-between' alignY='center'>
          <Stack grow='1' shrink='1' width='100%' align='center' alignY='center'>
            <DocumentTitle title={`Subgraph:${subgraph.name || 'Summary'}`} />
            <PageHeader crumbs={getMetadataDefaultCrumbs(Entity.Subgraph, projectName, projectId)} />
            <Container>
              <Tabs activeKey={activeTab} onChange={(tab) => changeTab(tab)}>
                {SubgraphPageLayout.tabs.map((v) => {
                  const title = v.title;
                  return (
                    <TabPane
                      key={title}
                      tab={title}
                      elementType='link'
                      to={Private_Routes.Subgraph.tab.getUrl({ uid, tab: title.toLowerCase() })}>
                      {getTabDetail(title)}
                    </TabPane>
                  );
                })}
              </Tabs>
            </Container>
          </Stack>
          <Footer>{projectId ? <GitInfo projectId={projectId} /> : null}</Footer>
        </Stack>
      </CodeGenerationContextProvider>
    </ProjectContextProvider>
  );
}
